import React, { useState, useEffect } from 'react';
import { Link as LinkIcon, Unlink, Users, FileText, TrendingUp, AlertCircle, RefreshCw, Search } from 'lucide-react';
import { PatientLinkingService, UserWithLinkStatus, PatientWithLinkStatus } from '../../services/PatientLinkingService';
import PatientUserLinkModal from '../../components/admin/PatientUserLinkModal';
import LinkedPatientBadge from '../../components/admin/LinkedPatientBadge';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { matchesPatientSearch } from '../../utils/patientSearch';

const PatientLinkingPage: React.FC = () => {
  const { selectedClient } = useAuth();
  const clientId = selectedClient?.clientId;
  const [users, setUsers] = useState<UserWithLinkStatus[]>([]);
  const [patients, setPatients] = useState<PatientWithLinkStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [selecteduserId, setSelecteduserId] = useState<string>();
  const [selectedPatientId, setSelectedPatientId] = useState<string>();
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [patientSearchTerm, setPatientSearchTerm] = useState('');
  const [filterLinked, setFilterLinked] = useState<'all' | 'linked' | 'unlinked'>('all');
  const [unlinkingPair, setUnlinkingPair] = useState<{ userId: string; patientId: string } | null>(null);
  const [isUnlinking, setIsUnlinking] = useState(false);

  const [stats, setStats] = useState({
    totalUsers: 0,
    totalPatients: 0,
    linkedUsers: 0,
    unlinkedUsers: 0,
    linkedPatients: 0,
    unlinkedPatients: 0
  });

  useEffect(() => {
    loadData();
  }, [clientId]);

  const loadData = async () => {
    if (!clientId) {
      setUsers([]);
      setPatients([]);
      setStats({
        totalUsers: 0,
        totalPatients: 0,
        linkedUsers: 0,
        unlinkedUsers: 0,
        linkedPatients: 0,
        unlinkedPatients: 0
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [usersData, patientsData, statsData] = await Promise.all([
        PatientLinkingService.getAllUsersWithLinkStatus(clientId),
        PatientLinkingService.getAllPatientsWithLinkStatus(clientId),
        PatientLinkingService.getLinkingStatistics(clientId)
      ]);
      setUsers(usersData);
      setPatients(patientsData);
      setStats(statsData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLinkModal = (userId?: string, patientId?: string) => {
    setSelecteduserId(userId);
    setSelectedPatientId(patientId);
    setShowLinkModal(true);
  };

  const handleCloseLinkModal = () => {
    setShowLinkModal(false);
    setSelecteduserId(undefined);
    setSelectedPatientId(undefined);
  };

  const handleUnlink = async () => {
    if (!unlinkingPair || !clientId) {
      toast.error('Dados de desvinculação inválidos');
      return;
    }

    try {
      setIsUnlinking(true);
      await PatientLinkingService.unlinkPatientFromUser(unlinkingPair.userId, unlinkingPair.patientId, clientId);
      toast.success('Vinculação removida com sucesso!');
      setUnlinkingPair(null);
      await loadData();
    } catch (error: any) {
      console.error('Erro ao desvincular:', error);
      toast.error(error.message || 'Erro ao desvincular');
    } finally {
      setIsUnlinking(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const searchLower = userSearchTerm.toLowerCase();
    const matchesSearch =
      user.email.toLowerCase().includes(searchLower) ||
      user.displayName.toLowerCase().includes(searchLower);

    const matchesFilter =
      filterLinked === 'all' ? true :
      filterLinked === 'linked' ? user.isLinked :
      !user.isLinked;

    return matchesSearch && matchesFilter;
  });

  const filteredPatients = patients.filter(patient => {
    const matchesSearch = matchesPatientSearch(
      patientSearchTerm,
      `${patient.nome} ${patient.sobrenome}`,
      [patient.prontuario, patient.email],
    );

    const matchesFilter =
      filterLinked === 'all' ? true :
      filterLinked === 'linked' ? patient.isLinked :
      !patient.isLinked;

    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vinculação de Pacientes</h1>
          <p className="text-gray-600">Gerencie a vinculação entre usuários e cadastros de pacientes</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={loadData}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </button>
          <button
            onClick={() => handleOpenLinkModal()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <LinkIcon className="w-4 h-4" />
            Nova Vinculação
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Usuários Pacientes</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
            </div>
            <Users className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Cadastros de Pacientes</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalPatients}</p>
            </div>
            <FileText className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Vinculações Ativas</p>
              <p className="text-2xl font-bold text-gray-900">{stats.linkedUsers}</p>
            </div>
            <LinkIcon className="w-8 h-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Taxa de Vinculação</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats.totalUsers > 0 ? Math.round((stats.linkedUsers / stats.totalUsers) * 100) : 0}%
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-orange-600" />
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Filtros</h2>
          <button
            onClick={() => {
              setUserSearchTerm('');
              setPatientSearchTerm('');
              setFilterLinked('all');
            }}
            className="text-sm text-blue-600 hover:text-blue-800"
          >
            Limpar Filtros
          </button>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={filterLinked}
              onChange={(e) => setFilterLinked(e.target.value as any)}
              className="w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos</option>
              <option value="linked">Apenas Vinculados</option>
              <option value="unlinked">Apenas Não Vinculados</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Usuários Pacientes
              </h2>
              <span className="text-sm text-gray-600">
                {filteredUsers.length} de {users.length}
              </span>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar por email ou nome..."
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="divide-y max-h-96 overflow-y-auto">
            {filteredUsers.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>Nenhum usuário encontrado</p>
              </div>
            ) : (
              filteredUsers.map(user => (
                <div key={user.uid} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-gray-900">{user.displayName || 'Sem nome'}</p>
                        <LinkedPatientBadge
                          isLinked={user.isLinked}
                          linkedPatientName={user.linkedPatientName}
                          size="sm"
                        />
                      </div>
                      <p className="text-sm text-gray-600">{user.email}</p>
                      {user.isLinked && user.linkedPatientName && (
                        <p className="text-xs text-gray-500 mt-1">
                          Vinculado a: {user.linkedPatientName}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {user.isLinked && user.linkedPatientId ? (
                        <button
                          onClick={() => setUnlinkingPair({ userId: user.uid, patientId: user.linkedPatientId! })}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Desvincular"
                        >
                          <Unlink className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleOpenLinkModal(user.uid)}
                          className="text-blue-600 hover:text-blue-800 p-1"
                          title="Vincular"
                        >
                          <LinkIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-green-600" />
                Cadastros de Pacientes
              </h2>
              <span className="text-sm text-gray-600">
                {filteredPatients.length} de {patients.length}
              </span>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Buscar por nome ou prontuário..."
                value={patientSearchTerm}
                onChange={(e) => setPatientSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="divide-y max-h-96 overflow-y-auto">
            {filteredPatients.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p>Nenhum paciente encontrado</p>
              </div>
            ) : (
              filteredPatients.map(patient => (
                <div key={patient.id} className="p-4 hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-gray-900">
                          {patient.nome} {patient.sobrenome}
                        </p>
                        <LinkedPatientBadge
                          isLinked={patient.isLinked}
                          linkedUserEmail={patient.linkedUserEmail}
                          size="sm"
                        />
                      </div>
                      <p className="text-sm text-gray-600">Prontuário: {patient.prontuario}</p>
                      {patient.email && (
                        <p className="text-xs text-gray-500">{patient.email}</p>
                      )}
                      {patient.isLinked && patient.linkedUserEmail && (
                        <p className="text-xs text-gray-500 mt-1">
                          Vinculado a: {patient.linkedUserEmail}
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {patient.isLinked && patient.linkeduserId ? (
                        <button
                          onClick={() => setUnlinkingPair({ userId: patient.linkeduserId!, patientId: patient.id })}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Desvincular"
                        >
                          <Unlink className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => handleOpenLinkModal(undefined, patient.id)}
                          className="text-green-600 hover:text-green-800 p-1"
                          title="Vincular"
                        >
                          <LinkIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showLinkModal && clientId && (
        <PatientUserLinkModal
          clientId={clientId}
          onClose={handleCloseLinkModal}
          onSuccess={loadData}
          preSelecteduserId={selecteduserId}
          preSelectedPatientId={selectedPatientId}
        />
      )}

      {unlinkingPair && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <Unlink className="w-8 h-8 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Confirmar Desvinculação</h3>
            </div>

            <p className="text-gray-600 mb-6">
              Tem certeza que deseja remover esta vinculação? Esta ação não pode ser desfeita.
            </p>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setUnlinkingPair(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                disabled={isUnlinking}
              >
                Cancelar
              </button>
              <button
                onClick={handleUnlink}
                disabled={isUnlinking}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isUnlinking ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Desvinculando...
                  </>
                ) : (
                  <>
                    <Unlink className="w-4 h-4" />
                    Confirmar Desvinculação
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientLinkingPage;
